import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { NotificationService, Notification } from '@app/core/services/notification.service';

interface ToastSegment {
  text: string;
  emphasis: boolean;
}

/** Toast global de notificações: sem ícones, auto-dismiss. */
@Component({
  selector: 'app-notification-toast',
  templateUrl: './notification-toast.component.html',
  styleUrls: ['./notification-toast.component.scss']
})
export class NotificationToastComponent implements OnInit, OnDestroy {
  notification: Notification | null = null;
  private subscription?: Subscription;
  private readonly emphasisPattern = /("[^"]+"|'[^']+'|`[^`]+`|\{[^}]+\}|\[[^\]]+\]|\b(?:ID|IDs|id|ids|código|codigo|protocolo|registro|matrícula|matricula)\s*(?:[:#]|n(?:º|°|o)\.?|número)?\s*[A-Z0-9][A-Z0-9._/-]*|\b[A-Z]{2,}(?:-\d+)+\b|\b[A-Z]{2,}-\d+\b|#\d+\b|\b[\w.-]+\.(?:pdf|doc|docx|txt|html|xls|xlsx|jpg|jpeg|png)\b)/gi;

  constructor(private notificationService: NotificationService) {}

  ngOnInit(): void {
    this.subscription = this.notificationService.notification$.subscribe(
      notification => {
        this.notification = notification;
      }
    );
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  close(): void {
    this.notificationService.clear();
  }

  get notificationTitle(): string {
    return this.normalizeInlineText(this.notification?.title ?? '');
  }

  get notificationMessageLines(): ToastSegment[][] {
    const message = this.notification?.message;
    if (!message) {
      return [];
    }

    return this.normalizeMessage(message)
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => this.tokenizeLine(line));
  }

  getContainerClasses(): string {
    if (!this.notification) return '';

    return this.notification.position === 'bottom'
      ? 'fixed bottom-6 left-1/2 -translate-x-1/2 z-[65] animate-slide-up px-4 w-full flex justify-center'
      : 'fixed top-16 left-1/2 -translate-x-1/2 z-[65] animate-slide-down px-4 w-full flex justify-center';
  }

  private applyAntiHighlight(text: string): string {
    const termosViloes = [
      'registros', 'matrícula', 'matricula', 'código', 'codigo', 
      'id', 'ids', 'protocolo', 'registro', 'número', 'numero'
    ];
    
    let formatado = text.replace(/[\u200B]/g, '');
    
    termosViloes.forEach(termo => {
      const termoInvisivel = termo.split('').join('\u200B');
      const regex = new RegExp(`\\b${termo}\\b`, 'gi');
      formatado = formatado.replace(regex, termoInvisivel);
    });
    
    return formatado;
  }

  private normalizeMessage(message: string): string {
    const mensagemProtegida = this.applyAntiHighlight(message);
    
    return mensagemProtegida
      // Remover caracteres de controle e espaços problemáticos
      // eslint-disable-next-line no-control-regex -- intencional: sanitiza chars de controle
      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '')
      // Converter espaços não-breaking em espaços normais
      .replace(/\u00A0/g, ' ')
      .replace(/\u2000-\u200A/g, ' ')
      // Remover soft hyphens
      .replace(/\u00AD/g, '')
      // Normalizar quebras de linha
      .replace(/\r\n/g, '\n')
      // Converter pipe em quebra de linha
      .replace(/\s*\|\s*/g, '\n')
      // Converter ponto-e-vírgula em quebra se seguido de letra maiúscula
      .replace(/\s*;\s*(?=[A-ZÀ-ÿ])/g, ';\n')
      // Normalizar múltiplos espaços em um só
      .replace(/[ \t]+/g, ' ')
      // Remover múltiplas quebras de linha
      .replace(/\n{2,}/g, '\n')
      .trim();
  }

  private normalizeInlineText(text: string): string {
    return text
      // eslint-disable-next-line no-control-regex -- intencional: sanitiza chars de controle
      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '')
      .replace(/\u00A0/g, ' ')
      .replace(/\u00AD/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private tokenizeLine(line: string): ToastSegment[] {
    const segments: ToastSegment[] = [];
    let lastIndex = 0;

    line.replace(this.emphasisPattern, (match, _group, offset: number) => {
      if (offset > lastIndex) {
        segments.push({ text: line.slice(lastIndex, offset), emphasis: false });
      }

      segments.push({ text: match, emphasis: true });
      lastIndex = offset + match.length;
      return match;
    });

    if (lastIndex < line.length) {
      segments.push({ text: line.slice(lastIndex), emphasis: false });
    }

    return segments.length ? segments : [{ text: line, emphasis: false }];
  }
}
